import { Injectable } from '@angular/core';
import {
	CachePolicy,
	CachePolicyEntity,
	camelToSnake,
	UrlService,
} from '@datachain/ui-sdk/common';
import { Observable } from 'rxjs';

import { DcBaseRequester } from '../core/dc-base.requester';
import { IMap, IMapResponse } from './map.model';

@Injectable({
	providedIn: 'root',
})
export class MapRequester extends DcBaseRequester<IMapResponse> {
	protected resource: string;
	protected elementsListUrl: string;
	protected singleElementUrl: string;

	public constructor(private readonly urlService: UrlService) {
		super();
		this.resource = this.generateResourceUrl('MAPS_GET_ALL');
		this.elementsListUrl = this.resource;
		this.singleElementUrl = this.resource;
	}

	public getMapList(useCache = true): Observable<ReadonlyArray<IMapResponse>> {
		const resourceUrl = this.generateResourceUrl('MAPS_GET_ALL_DETAILED');

		const url = this.urlService.generateUrl(
			resourceUrl,
			{},
			{
				project: this.projectId,
			}
		);
		const cachePolicy = useCache
			? CachePolicyEntity.build({
					policy: CachePolicy.NetworkAndCache,
					maxAgeInSeconds: 60,
					isCacheable: true,
			  })
			: CachePolicyEntity.build({
					policy: CachePolicy.NetworkOnly,
					maxAgeInSeconds: 0,
					isCacheable: false,
			  });
		return this.get<Array<IMapResponse>>(url, cachePolicy);
	}

	public getMap(mapId: number): Observable<IMap> {
		const resourceUrl = this.generateResourceUrl('MAPS_GET');
		const url = this.urlService.generateUrl(
			resourceUrl,
			{
				id: mapId,
			},
			{
				project: this.projectId,
			}
		);
		return this.http.get<IMap>(url);
	}

	public changeActivationStatusMap(map: IMap): Observable<void> {
		const resourceUrl = this.generateResourceUrl('MAPS_EDIT');
		const url = this.urlService.generateUrl(
			resourceUrl,
			{
				id: map.id,
			},
			{
				project: this.projectId,
			}
		);
		return this.http.put<void>(url, {
			metadata: {
				active: map.metadata.actif,
				[camelToSnake('shortLabel')]: map.metadata.label,
			},
		});
	}

	public deleteMap(mapId: number): Observable<void> {
		const resourceUrl = this.generateResourceUrl('MAPS_DELETE');
		const url = this.urlService.generateUrl(
			resourceUrl,
			{
				id: mapId,
			},
			{
				project: this.projectId,
			}
		);
		return this.http.delete<void>(url);
	}

	public deleteManyMaps(mapIds: Array<number>): Observable<void> {
		const resourceUrl = this.generateResourceUrl('MAPS_DELETE_BULK');

		const url = this.urlService.generateUrl(
			resourceUrl,
			{},
			{
				project: this.projectId,
				mapsId: mapIds,
			}
		);
		return this.http.delete<void>(url);
	}

	public duplicateMap(mapId: number): Observable<number> {
		const resourceUrl = this.generateResourceUrl('MAPS_DUPLICATE');
		const url = this.urlService.generateUrl(
			resourceUrl,
			{
				id: mapId,
			},
			{
				project: this.projectId,
			}
		);

		return this.http.post<number>(url, {});
	}
}
